import { useCallback, useEffect, useState } from 'react';
import {
    ResourceVisibility,
    getResourceVisibility,
} from '../services/resourceVisibilityService';

interface UseResourceVisibilityReturn {
    visibility: ResourceVisibility | null;
    loading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

/**
 * Loads the current user's school resource visibility flags. Consumers should
 * treat a null value (still loading or failed) as "show everything" so content
 * is never hidden by accident while the flags are unavailable.
 */
export function useResourceVisibility(): UseResourceVisibilityReturn {
    const [visibility, setVisibility] = useState<ResourceVisibility | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchVisibility = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getResourceVisibility();
            setVisibility(data);
            setError(null);
        } catch (err) {
            setError(err as Error);
            setVisibility(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchVisibility();
    }, [fetchVisibility]);

    return { visibility, loading, error, refetch: fetchVisibility };
}
